import {
  HabitFrequencyType,
  HabitTargetType,
  SpecialDays,
  TargetDirection,
  TargetEvaluation,
  WeekDay,
} from '../dto/habit.enum';

export enum HabitCategory {
  RELATION_TO_GOD = 'Relation to God',
  RELATION_TO_YOUR_NEIGHBOR = 'Relation to Your Neighbor',
  RELATION_TO_YOURSELF = 'Relation to Yourself',
  OWN_HABIT = 'Own Habit',
}

interface HabitConfigEntry {
  name: HabitCategory;
  habits: HabitConfig[];
}

export interface HabitConfig {
  title: string;
  description?: string;
  notes?: string;
  category?: HabitCategory;

  targetType: HabitTargetType;
  targetConfig?: TargetConfig;

  frequencyType: HabitFrequencyType;
  frequencyConfig?: FrequencyConfig;
}

export interface TargetConfig {
  evaluation?: TargetEvaluation;
  direction?: TargetDirection;
  value?: number;
  valueHint?: string;
}

export interface FrequencyConfig {
  count?: number;
  weekDays?: WeekDay[];
  monthDays?: number[];
  specialDays?: SpecialDays;
}

export const PredefinedHabits: HabitConfigEntry[] = [
  {
    name: HabitCategory.RELATION_TO_GOD,
    habits: [
      {
        title: 'Morning Prayer',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Start your day with God, the goal of your existence, and every action gains value!',
      },
      {
        title: 'Evening Prayer',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Conclude your day with God, the giver of every good gift: gratitude prepares even greater favour!',
      },
      {
        title: 'Rosary',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Meditate on the benefits of God in the Incarnation, Redemption and Consummation: you will grow in the knowledge and love of God through the individual decades!',
      },
      {
        title: 'KJB Prayers',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          "Unite in prayer with the other KJB'lers, so you build up the kingdom of God and strengthen the community; you also receive a share in the prayers of others!",
      },
      {
        title: 'Living in the Presence of God',
        targetType: HabitTargetType.QUANTITY,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'The Most Holy Trinity dwells in your heart through grace: think of this high guest with gratitude, greet Him often throughout the day and show Him your love.',
      },
      {
        title: 'Meditation',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Take a certain time during the day, read a short spiritual text and consider its meaning, then enter into a dialogue with Christ in gratitude and love!',
      },
      {
        title: 'Spiritual Reading',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Take a certain time during the day to read a spiritual text: the knowledge you gain will make it easier for you to orientate your life towards God and to love Him.',
      },
      {
        title: 'Going to Mass',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.WEEKLY,
        description:
          'The Holy Mass is the centre of life: take part in it and by making the Redemption present you will be richly blessed and united with Christ through Holy Communion.',
      },
      {
        title: 'Confession',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.MONTHLY,
        description:
          'Confession not only washes away sins, but also promotes self-knowledge and gives us graces to move forward: use this sacrament once a month for spiritual growth!',
      },
      {
        title: 'Ember days',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.SPECIAL_DAYS,
        frequencyConfig: {
          specialDays: SpecialDays.EMBER,
        },
        description:
          '"The harvest is plentiful, but the laborers are few. Ask the Lord of the harvest to send laborers into his harvest." (Lk 10:2)\nIn union with the whole of Christendom, keep the ember days in fasting for holy priests, that they may lead the people to God!',
      },
      {
        title: 'First Friday Devotion',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.SPECIAL_DAYS,
        frequencyConfig: {
          specialDays: SpecialDays.HEART_JESUS_FRIDAY,
        },
        description:
          '"In the abundance of the mercy of my heart, I promise you that in my almighty love I will grant the grace of a penitential end to all who worthily receive Holy Communion on the first Fridays of nine consecutive months, so that they will not die without the sacraments."\nRespond to this promise of Christ to St. Margaret Mary Alacoque!',
      },
      {
        title: 'Heart of Mary Saturday of Atonement',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.SPECIAL_DAYS,
        frequencyConfig: {
          specialDays: SpecialDays.HEART_MARY_SATURDAY,
        },
        description:
          '"My daughter, look at my heart, surrounded by thorns with which ungrateful people constantly pierce it with their blasphemies and ingratitude. At least you try to console me and tell me that I promise to assist all those at the hour of death with all the graces necessary for the salvation of these souls who confess on the first Saturday of each month for five months, receive Holy Communion, pray a rosary and keep me company for 15 minutes by meditating on the 15 mysteries of the rosary with the intention of making reparation to me."\nRespond to Mary\'s promise to Sister Lucia!',
      },
    ],
  },
  {
    name: HabitCategory.RELATION_TO_YOUR_NEIGHBOR,
    habits: [
      {
        title: 'Show Good Mood',
        targetType: HabitTargetType.RATING,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          "Learn to control your mood: Christ is a reason for joy: remember Paul's call: Rejoice in the Lord always.",
      },
      {
        title: 'Give Attention',
        targetType: HabitTargetType.QUANTITY,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Your neighbors are valuable and kind: show your appreciation through small gestures!',
      },
      {
        title: 'Ave Maria for People I Struggle',
        targetType: HabitTargetType.QUANTITY,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          "Build true charity by praying three Hail Mary's for the three people you have the most trouble with!",
      },
      {
        title: 'Show Gratitude',
        targetType: HabitTargetType.RATING,
        frequencyType: HabitFrequencyType.DAILY,
        description: "Don't take things for granted: show appreciation by giving thanks!",
      },
      {
        title: 'Help Someone',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Love is shown not so much in words, but in deeds: help people in your neighborhood!',
      },
      {
        title: 'Spend Time with Someone',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Love can be expressed in different ways: one way is to give your time: do it intentionally!',
      },
      {
        title: 'Cultivate Friendship',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Good friendship will take you a long way: choose virtuous people to spend time with and do things together.',
      },
    ],
  },
  {
    name: HabitCategory.RELATION_TO_YOURSELF,
    habits: [
      {
        title: 'Screen Time',
        targetType: HabitTargetType.QUANTITY,
        targetConfig: { direction: TargetDirection.REDUCTION },
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Screen time often robs us of valuable time and joy of life: set yourself a time limit to be freer for the good things and people around you.',
      },
      {
        title: 'Sport',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.WEEKLY,
        description:
          'Strengthen your will, build up your health and work on your inner balance through regular exercise!',
      },
      {
        title: 'Sleep Time',
        targetType: HabitTargetType.QUANTITY,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Your body needs sleep for health, balance and performance: set yourself a specific time when you go to bed.',
      },
      {
        title: 'Get Straight Out of Bed',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          "When your alarm goes off, it's time to get up, because you need to work on building big things: not a minute longer in bed!",
      },
      {
        title: 'Tidy Up',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.WEEKLY,
        description: 'Outer order helps you to achieve inner order and peace: work daily to build and maintain it.',
      },
      {
        title: 'Handling Addiction',
        targetType: HabitTargetType.RATING,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Addictions can ruin your life: reduce false dependence slowly and systematically!',
      },
      {
        title: 'Reading a Book',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Acquire wisdom and find some rest through daily reading.',
      },
      {
        title: 'Practising Virtue',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.WEEKLY,
        description: 'Virtue makes you even more lovable: choose a virtue that you build up daily through small acts.',
      },
      {
        title: 'Renounce',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description:
          'Strengthen your will by deliberately giving up certain things such as snacks or sweets: this will make you freer for God!',
      },
      {
        title: 'Fasting',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Take only a small snack instead of a meal: you will become freer for spiritual things and God!',
      },
    ],
  },
  {
    name: HabitCategory.OWN_HABIT,
    habits: [
      {
        title: 'Yes/No Habit',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'A habit that you can clearly assess with a yes or no.',
      },
      {
        title: 'Rating Habit',
        targetType: HabitTargetType.RATING,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'A habit that you can rate on a scale of 1-10.',
      },
      {
        title: 'Quantity Habit',
        targetType: HabitTargetType.QUANTITY,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'A habit that is associated with a certain value: minutes, units, repetitions, etc.',
      },
    ],
  },
];

export interface HabitBundle {
  name: string;
  availableFrom?: Date;
  availableTo?: Date;
  habits: HabitConfig[];
}

export const HabitBundles: HabitBundle[] = [
  {
    name: 'Advent Package',
    availableTo: new Date('2025-12-24T11:00:00Z'),
    habits: [
      {
        title: 'Reduce screen time',
        targetType: HabitTargetType.QUANTITY,
        targetConfig: {
          direction: TargetDirection.REDUCTION,
          evaluation: TargetEvaluation.DAILY,
          valueHint: 'Time in minutes',
        },
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Consciously reduce your screen time or completely avoid certain apps.',
      },
      {
        title: 'Additional time with God',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Take 10 minutes daily for contemplation or spiritual reading.',
        notes: '10 minutes contemplation or spiritual reading',
      },
      {
        title: 'Daily good deed',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Do a good deed every day for someone around you.',
        notes: 'A good deed for someone around you',
      },
    ],
  },
  {
    name: 'Lent Package',
    availableTo: new Date('2026-04-04T11:00:00Z'),
    habits: [
      {
        title: 'Abstaining from YouTube and social media',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Abstain from YouTube/social media or strongly limit their use.',
        notes: 'No YouTube or social media today',
      },
      {
        title: 'Spiritual reading',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Take 10 minutes daily for spiritual reading.',
        notes: '10 minutes of spiritual reading',
      },
      {
        title: 'Daily sacrifice',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Offer a small sacrifice each day to unite yourself to Christ.',
      },
      {
        title: 'One decade of the Rosary',
        targetType: HabitTargetType.BOOLEAN,
        frequencyType: HabitFrequencyType.DAILY,
        description: 'Pray one decade of the Rosary for the conversion of a Muslim.',
        notes: 'One decade for the conversion of a Muslim',
      },
    ],
  },
];
